import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, Database, Bell, CheckCircle, XCircle, MessageSquare, Eye, EyeOff, Save, RefreshCw, Phone, Clock, Mail, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  const [columnRange, setColumnRange] = useState('A:ZZ');
  const [advisorColumn, setAdvisorColumn] = useState('conseiller');
  const [sheetColumns, setSheetColumns] = useState([]);

  const [credentials, setCredentials] = useState({
    twilio: { account_sid: '', auth_token: '', phone_number: '', configured: false },
    mailjet: { api_key: '', secret_key: '', sender_email: '', configured: false },
    postmark: { server_token: '', sender_email: '', configured: false }
  });
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showMailjetSecret, setShowMailjetSecret] = useState(false);
  const [showPostmarkToken, setShowPostmarkToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // ── Account settings (alert email, timezone, calling calendar, weekly recap) ──
  const [accountSettings, setAccountSettings] = useState({
    alert_email: '',
    timezone: 'Europe/Paris',
    calling_calendar: { active_days: [0, 1, 2, 3, 4], start_time: '09:00', end_time: '18:00' },
    weekly_recap: { enabled: true, day_of_week: 0, time: '09:00' },
    resolved_alert_email: '',
    google_review_sms_template_id: '',
    google_review_email_template_id: '',
    callback_alert_email_template_id: '',
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [emailTemplatesList, setEmailTemplatesList] = useState([]);
  const [smsTemplatesList, setSmsTemplatesList] = useState([]);

  useEffect(() => {
    checkSheetsConnection();
    loadCredentials();
    loadSheetSettings();
    loadAccountSettings();
  }, []);

  const loadAccountSettings = async () => {
    try {
      const res = await axios.get(`${API}/account-settings`);
      setAccountSettings({
        alert_email: res.data.alert_email || '',
        timezone: res.data.timezone || 'Europe/Paris',
        calling_calendar: res.data.calling_calendar || { active_days: [0,1,2,3,4], start_time: '09:00', end_time: '18:00' },
        weekly_recap: res.data.weekly_recap || { enabled: true, day_of_week: 0, time: '09:00' },
        resolved_alert_email: res.data.resolved_alert_email || '',
        google_review_sms_template_id: res.data.google_review_sms_template_id || '',
        google_review_email_template_id: res.data.google_review_email_template_id || '',
        callback_alert_email_template_id: res.data.callback_alert_email_template_id || '',
        company_name: res.data.company_name || '',
        company_logo: res.data.company_logo || '',
      });
    } catch (err) {
      console.error('Error loading account settings:', err);
    }
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/automations/email-templates/list`),
        axios.get(`${API}/automations/sms-templates/list`),
      ]);
      setEmailTemplatesList(emailRes.data || []);
      setSmsTemplatesList(smsRes.data || []);
    } catch (err) {
      console.error('Error loading template lists:', err);
    }
  };

  // Marque blanche (#11) — le logo part en data: URI dans les réglages du compte.
  // Borne côté navigateur pour donner une erreur claire avant l'appel serveur.
  const MAX_LOGO_BYTES = 200 * 1024;

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Logo trop lourd (200 Ko maximum). Réduisez l\'image avant de l\'importer.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAccountSettings((prev) => ({ ...prev, company_logo: reader.result }));
    reader.onerror = () => toast.error('Impossible de lire ce fichier.');
    reader.readAsDataURL(file);
  };

  const handleSaveAccountSettings = async () => {
    setSavingAccount(true);
    try {
      const payload = {
        alert_email: accountSettings.alert_email || null,
        timezone: accountSettings.timezone,
        calling_calendar: accountSettings.calling_calendar,
        weekly_recap: accountSettings.weekly_recap,
        google_review_sms_template_id: accountSettings.google_review_sms_template_id || null,
        google_review_email_template_id: accountSettings.google_review_email_template_id || null,
        callback_alert_email_template_id: accountSettings.callback_alert_email_template_id || null,
        company_name: accountSettings.company_name || null,
        company_logo: accountSettings.company_logo || null,
      };
      const res = await axios.put(`${API}/account-settings`, payload);
      setAccountSettings({
        alert_email: res.data.alert_email || '',
        timezone: res.data.timezone,
        calling_calendar: res.data.calling_calendar,
        weekly_recap: res.data.weekly_recap,
        resolved_alert_email: res.data.resolved_alert_email || '',
        google_review_sms_template_id: res.data.google_review_sms_template_id || '',
        google_review_email_template_id: res.data.google_review_email_template_id || '',
        callback_alert_email_template_id: res.data.callback_alert_email_template_id || '',
        company_name: res.data.company_name || '',
        company_logo: res.data.company_logo || '',
      });
      toast.success('Réglages enregistrés');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(detail);
    } finally {
      setSavingAccount(false);
    }
  };

  const toggleActiveDay = (day) => {
    setAccountSettings((prev) => {
      const set = new Set(prev.calling_calendar.active_days);
      set.has(day) ? set.delete(day) : set.add(day);
      return {
        ...prev,
        calling_calendar: { ...prev.calling_calendar, active_days: [...set].sort((a, b) => a - b) },
      };
    });
  };

  const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const TIMEZONES = [
    'Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Madrid',
    'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'UTC',
  ];

  const loadCredentials = async () => {
    try {
      const response = await axios.get(`${API}/admin/credentials`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Error loading credentials:', error);
    }
  };

  const checkSheetsConnection = async () => {
    try {
      const response = await axios.get(`${API}/oauth/sheets/status`);
      setSheetsConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking sheets connection:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSheetSettings = async () => {
    try {
      const response = await axios.get(`${API}/automations/sheet-settings`);
      if (response.data) {
        if (response.data.spreadsheet_id) setSpreadsheetId(response.data.spreadsheet_id);
        if (response.data.sheet_name) setSheetName(response.data.sheet_name);
        if (response.data.advisor_column) setAdvisorColumn(response.data.advisor_column);

        if (response.data.spreadsheet_id) {
          loadSheetColumns(response.data.spreadsheet_id, response.data.sheet_name || 'Sheet1');
        }
      }
    } catch (error) {
      console.error('Error loading sheet settings:', error);
    }
  };

  const loadSheetColumns = async (sheetId, tabName) => {
    try {
      const response = await axios.get(
        `${API}/automations/sheet-columns?spreadsheet_id=${sheetId}&sheet_name=${tabName}`
      );
      setSheetColumns(response.data || []);
    } catch (error) {
      console.error('Error loading sheet columns:', error);
    }
  };

  const saveSheetSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/automations/sheet-settings`, {
        spreadsheet_id: spreadsheetId,
        sheet_name: sheetName,
        advisor_column: advisorColumn
      });

      toast.success('Paramètres du Sheet enregistrés !');

      if (spreadsheetId) {
        loadSheetColumns(spreadsheetId, sheetName);
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const testSync = async () => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (spreadsheetId) {
        const fullRange = `${sheetName}!${columnRange}`;
        params.append('spreadsheet_id', spreadsheetId);
        params.append('range_name', fullRange);
      }

      const response = await axios.post(`${API}/sync/google-sheets?${params.toString()}`, {});

      const data = response.data;
      let message = `Synchronisation réussie !\n`;
      message += `${data.clients_added} clients ajoutés\n`;
      message += `${data.clients_updated || 0} mis à jour\n`;

      if (data.clients_skipped > 0) {
        message += `${data.clients_skipped} lignes ignorées\n`;
      }
      if (data.total_rows) {
        message += `Total lignes lues: ${data.total_rows}`;
      }

      toast.success(message, { duration: 8000 });
    } catch (error) {
      toast.error('Erreur lors de la synchronisation. Vérifiez vos paramètres.');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveTwilio = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/credentials`, {
        service: 'twilio',
        credentials: {
          account_sid: credentials.twilio.account_sid,
          auth_token: credentials.twilio.auth_token,
          phone_number: credentials.twilio.phone_number
        }
      });
      toast.success('Identifiants Twilio mis à jour !');
      loadCredentials();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMailjet = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/credentials`, {
        service: 'mailjet',
        credentials: {
          api_key: credentials.mailjet.api_key,
          secret_key: credentials.mailjet.secret_key,
          sender_email: credentials.mailjet.sender_email
        }
      });
      toast.success('Identifiants Mailjet mis à jour !');
      loadCredentials();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePostmark = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/credentials`, {
        service: 'postmark',
        credentials: {
          server_token: credentials.postmark.server_token,
          sender_email: credentials.postmark.sender_email
        }
      });
      toast.success('Identifiants Postmark mis à jour !');
      loadCredentials();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight text-foreground">
          Paramètres
        </h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Configurez vos intégrations et paramètres
        </p>
      </div>

      <Tabs defaultValue="google-sheets" className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-6">
          <TabsTrigger value="google-sheets">Google Sheets</TabsTrigger>
          <TabsTrigger value="sms">SMS (Twilio)</TabsTrigger>
          <TabsTrigger value="email-mailjet">Mailjet</TabsTrigger>
          <TabsTrigger value="email-postmark">Postmark</TabsTrigger>
          <TabsTrigger value="calls-alerts">Appels & Alertes</TabsTrigger>
          <TabsTrigger value="marque">Marque</TabsTrigger>
        </TabsList>

        {/* ─── GOOGLE SHEETS TAB ─── */}
        <TabsContent value="google-sheets" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Database className="text-primary" size={24} />
                Google Sheets
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Connection status */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  ) : sheetsConnected ? (
                    <>
                      <CheckCircle className="text-green-500" size={20} />
                      <div>
                        <p className="font-medium text-foreground text-sm">Google Sheets connecté</p>
                        <p className="text-xs text-muted-foreground">Accès autorisé lors de votre connexion Google</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-muted-foreground" size={20} />
                      <div>
                        <p className="font-medium text-foreground text-sm">Non connecté</p>
                        <p className="text-xs text-muted-foreground">Reconnectez-vous pour autoriser l'accès Google Sheets</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Sheet config — always visible when connected */}
              {!loading && sheetsConnected && (
                <>
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-sm text-muted-foreground font-medium mb-2">
                      Comment obtenir l'ID de votre Google Sheet :
                    </p>
                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>Ouvrez votre Google Sheet</li>
                      <li>L'ID se trouve dans l'URL : docs.google.com/spreadsheets/d/<strong>[ID]</strong>/edit</li>
                      <li>Copiez l'ID et collez-le ci-dessous</li>
                    </ol>
                  </div>

                  <div>
                    <Label htmlFor="spreadsheet_id">ID du Google Sheet</Label>
                    <Input
                      id="spreadsheet_id"
                      value={spreadsheetId}
                      onChange={(e) => setSpreadsheetId(e.target.value)}
                      placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                      data-testid="spreadsheet-id-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sheet_name">Nom de l'onglet</Label>
                    <Input
                      id="sheet_name"
                      value={sheetName}
                      onChange={(e) => setSheetName(e.target.value)}
                      placeholder="Sheet1"
                      data-testid="sheet-name-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Le nom visible en bas de votre feuille (ex: Sheet1, Feuille1, Clients...)
                    </p>
                  </div>

                  <div className="p-4 rounded-lg border border-border/40 bg-muted/20">
                    <p className="text-sm text-foreground font-medium mb-3">
                      Configuration des Expéditeurs
                    </p>
                    <div>
                      <Label htmlFor="advisor_column">Colonne "Conseiller" dans votre Sheet</Label>
                      {sheetColumns.length > 0 ? (
                        <select
                          id="advisor_column"
                          value={advisorColumn}
                          onChange={(e) => setAdvisorColumn(e.target.value)}
                          className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="advisor-column-select"
                        >
                          <option value="">-- Sélectionner une colonne --</option>
                          {sheetColumns.map((col) => (
                            <option key={col.name} value={col.name}>
                              {col.name} {col.sample_values?.length > 0 ? `(ex: ${col.sample_values[0]})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          id="advisor_column"
                          value={advisorColumn}
                          onChange={(e) => setAdvisorColumn(e.target.value)}
                          placeholder="conseiller"
                          data-testid="advisor-column-input"
                        />
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Cette colonne permet d'associer chaque client à un expéditeur
                      </p>
                    </div>
                    <Button
                      onClick={saveSheetSettings}
                      disabled={saving}
                      className="mt-3 rounded-full"
                      data-testid="save-sheet-settings-btn"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                    </Button>
                  </div>

                  <Button
                    onClick={testSync}
                    disabled={syncing || !spreadsheetId}
                    className="rounded-full w-full"
                    variant={spreadsheetId ? "default" : "outline"}
                    data-testid="test-sync-button"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Synchronisation en cours...' : 'Synchroniser maintenant'}
                  </Button>
                </>
              )}

              {!loading && !sheetsConnected && (
                <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                  <p className="text-sm text-foreground font-medium mb-1">
                    Accès Google Sheets requis
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Déconnectez-vous puis reconnectez-vous pour autoriser l'accès à vos Google Sheets.
                    L'autorisation est demandée une seule fois lors de la connexion.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── TWILIO TAB ─── */}
        <TabsContent value="sms" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <MessageSquare className="text-primary" size={24} />
                Twilio (SMS)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {credentials.twilio.configured && (
                <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/10 text-sm text-green-600 dark:text-green-400">
                  Twilio est configuré et opérationnel
                </div>
              )}

              <div>
                <Label htmlFor="twilio_sid">Account SID</Label>
                <Input
                  id="twilio_sid"
                  value={credentials.twilio.account_sid}
                  onChange={(e) => setCredentials({
                    ...credentials,
                    twilio: { ...credentials.twilio, account_sid: e.target.value }
                  })}
                  placeholder="AC..."
                  data-testid="twilio-sid-input"
                />
              </div>

              <div>
                <Label htmlFor="twilio_token">Auth Token</Label>
                <div className="relative">
                  <Input
                    id="twilio_token"
                    type={showTwilioToken ? "text" : "password"}
                    value={credentials.twilio.auth_token}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      twilio: { ...credentials.twilio, auth_token: e.target.value }
                    })}
                    placeholder="Votre Auth Token"
                    data-testid="twilio-token-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTwilioToken(!showTwilioToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showTwilioToken ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="twilio_phone">Numéro de téléphone</Label>
                <Input
                  id="twilio_phone"
                  value={credentials.twilio.phone_number}
                  onChange={(e) => setCredentials({
                    ...credentials,
                    twilio: { ...credentials.twilio, phone_number: e.target.value }
                  })}
                  placeholder="+33..."
                  data-testid="twilio-phone-input"
                />
              </div>

              <Button
                onClick={handleSaveTwilio}
                disabled={saving}
                className="rounded-full w-full"
                data-testid="save-twilio-button"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder Twilio'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── MAILJET TAB ─── */}
        <TabsContent value="email-mailjet" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Bell className="text-primary" size={24} />
                Mailjet (Email)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {credentials.mailjet.configured && (
                <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/10 text-sm text-green-600 dark:text-green-400">
                  Mailjet est configuré et opérationnel
                </div>
              )}

              <div>
                <Label htmlFor="mailjet_key">API Key (clé publique)</Label>
                <Input
                  id="mailjet_key"
                  value={credentials.mailjet.api_key}
                  onChange={(e) => setCredentials({
                    ...credentials,
                    mailjet: { ...credentials.mailjet, api_key: e.target.value }
                  })}
                  placeholder="Votre API Key"
                  data-testid="mailjet-key-input"
                />
              </div>

              <div>
                <Label htmlFor="mailjet_secret">Secret Key (clé privée)</Label>
                <div className="relative">
                  <Input
                    id="mailjet_secret"
                    type={showMailjetSecret ? "text" : "password"}
                    value={credentials.mailjet.secret_key}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      mailjet: { ...credentials.mailjet, secret_key: e.target.value }
                    })}
                    placeholder="Votre Secret Key"
                    data-testid="mailjet-secret-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowMailjetSecret(!showMailjetSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showMailjetSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="mailjet_sender">Email expéditeur</Label>
                <Input
                  id="mailjet_sender"
                  type="email"
                  value={credentials.mailjet.sender_email}
                  onChange={(e) => setCredentials({
                    ...credentials,
                    mailjet: { ...credentials.mailjet, sender_email: e.target.value }
                  })}
                  placeholder="noreply@votredomaine.com"
                  data-testid="mailjet-sender-input"
                />
              </div>

              <div className="p-3 rounded-lg border border-border/40 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  Assurez-vous que le domaine de l'email expéditeur est vérifié dans votre compte Mailjet.
                </p>
              </div>

              <Button
                onClick={handleSaveMailjet}
                disabled={saving}
                className="rounded-full w-full"
                data-testid="save-mailjet-button"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder Mailjet'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── POSTMARK TAB ─── */}
        <TabsContent value="email-postmark" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Bell className="text-primary" size={24} />
                Postmark (Email)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {credentials.postmark.configured && (
                <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/10 text-sm text-green-600 dark:text-green-400">
                  Postmark est configuré et opérationnel
                </div>
              )}

              {credentials.mailjet.configured && credentials.postmark.configured && (
                <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-foreground">
                  Postmark est prioritaire sur Mailjet quand les deux sont configurés.
                </div>
              )}

              <div>
                <Label htmlFor="postmark_token">Server Token</Label>
                <div className="relative">
                  <Input
                    id="postmark_token"
                    type={showPostmarkToken ? "text" : "password"}
                    value={credentials.postmark.server_token}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      postmark: { ...credentials.postmark, server_token: e.target.value }
                    })}
                    placeholder="Votre Server Token Postmark"
                    data-testid="postmark-token-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPostmarkToken(!showPostmarkToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPostmarkToken ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Disponible dans Postmark &gt; Servers &gt; API Tokens
                </p>
              </div>

              <div>
                <Label htmlFor="postmark_sender">Email expéditeur</Label>
                <Input
                  id="postmark_sender"
                  type="email"
                  value={credentials.postmark.sender_email}
                  onChange={(e) => setCredentials({
                    ...credentials,
                    postmark: { ...credentials.postmark, sender_email: e.target.value }
                  })}
                  placeholder="noreply@votredomaine.com"
                  data-testid="postmark-sender-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Doit correspondre à une Sender Signature vérifiée dans Postmark
                </p>
              </div>

              <Button
                onClick={handleSavePostmark}
                disabled={saving}
                className="rounded-full w-full"
                data-testid="save-postmark-button"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder Postmark'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── CALLS & ALERTS TAB ─── */}
        <TabsContent value="calls-alerts" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Mail className="text-primary" size={22} />
                Email d'alerte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Label htmlFor="alert-email">Email destinataire des alertes</Label>
              <Input
                id="alert-email"
                type="email"
                value={accountSettings.alert_email}
                onChange={(e) => setAccountSettings({ ...accountSettings, alert_email: e.target.value })}
                placeholder={accountSettings.resolved_alert_email || 'votre@email.com'}
                data-testid="alert-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour utiliser votre email de connexion ({accountSettings.resolved_alert_email || '—'}).
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Clock className="text-primary" size={22} />
                Calendrier d'ouverture
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Fuseau horaire</Label>
                <Select
                  value={accountSettings.timezone}
                  onValueChange={(v) => setAccountSettings({ ...accountSettings, timezone: v })}
                >
                  <SelectTrigger data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Jours d'ouverture</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {DAY_LABELS.map((label, day) => (
                    <label key={day} className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-accent">
                      <Checkbox
                        checked={accountSettings.calling_calendar.active_days.includes(day)}
                        onCheckedChange={() => toggleActiveDay(day)}
                        data-testid={`active-day-${day}`}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-time">Heure de début</Label>
                  <Input
                    id="start-time"
                    type="time"
                    value={accountSettings.calling_calendar.start_time}
                    onChange={(e) => setAccountSettings({
                      ...accountSettings,
                      calling_calendar: { ...accountSettings.calling_calendar, start_time: e.target.value },
                    })}
                    data-testid="start-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end-time">Heure de fin</Label>
                  <Input
                    id="end-time"
                    type="time"
                    value={accountSettings.calling_calendar.end_time}
                    onChange={(e) => setAccountSettings({
                      ...accountSettings,
                      calling_calendar: { ...accountSettings.calling_calendar, end_time: e.target.value },
                    })}
                    data-testid="end-time-input"
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Aucune communication (appel, email, SMS) ne sera envoyée en dehors de ces créneaux.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Phone className="text-primary" size={22} />
                Récapitulatif hebdomadaire
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="recap-enabled">Envoyer le récap hebdomadaire</Label>
                <Switch
                  id="recap-enabled"
                  checked={accountSettings.weekly_recap.enabled}
                  onCheckedChange={(v) => setAccountSettings({
                    ...accountSettings,
                    weekly_recap: { ...accountSettings.weekly_recap, enabled: v },
                  })}
                  data-testid="recap-enabled-switch"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Jour</Label>
                  <Select
                    value={String(accountSettings.weekly_recap.day_of_week)}
                    onValueChange={(v) => setAccountSettings({
                      ...accountSettings,
                      weekly_recap: { ...accountSettings.weekly_recap, day_of_week: parseInt(v, 10) },
                    })}
                  >
                    <SelectTrigger data-testid="recap-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_LABELS.map((label, day) => (
                        <SelectItem key={day} value={String(day)}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="recap-time">Heure</Label>
                  <Input
                    id="recap-time"
                    type="time"
                    value={accountSettings.weekly_recap.time}
                    onChange={(e) => setAccountSettings({
                      ...accountSettings,
                      weekly_recap: { ...accountSettings.weekly_recap, time: e.target.value },
                    })}
                    data-testid="recap-time-input"
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Un email récapitulant les clients à rappeler vous sera envoyé chaque semaine.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <MessageSquare className="text-primary" size={22} />
                Templates des appels
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Template SMS — Avis Google</Label>
                <Select
                  value={accountSettings.google_review_sms_template_id || '__default__'}
                  onValueChange={(v) => setAccountSettings({
                    ...accountSettings,
                    google_review_sms_template_id: v === '__default__' ? '' : v,
                  })}
                >
                  <SelectTrigger data-testid="google-sms-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">(texte par défaut)</SelectItem>
                    {smsTemplatesList.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  SMS envoyé au client satisfait (numéro mobile) avec le lien d'avis Google.
                  Variables : <code>{'{first_name}'}</code>, <code>{'{review_url}'}</code>, <code>{'{client_name}'}</code>…
                </p>
              </div>

              <div>
                <Label>Template Email — Avis Google</Label>
                <Select
                  value={accountSettings.google_review_email_template_id || '__default__'}
                  onValueChange={(v) => setAccountSettings({
                    ...accountSettings,
                    google_review_email_template_id: v === '__default__' ? '' : v,
                  })}
                >
                  <SelectTrigger data-testid="google-email-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">(texte par défaut)</SelectItem>
                    {emailTemplatesList.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Email envoyé au client satisfait (numéro fixe) avec le lien d'avis Google.
                  Variables : <code>{'{first_name}'}</code>, <code>{'{review_url}'}</code>, <code>{'{client_name}'}</code>…
                </p>
              </div>

              <div>
                <Label>Template Email — Alerte rappel</Label>
                <Select
                  value={accountSettings.callback_alert_email_template_id || '__default__'}
                  onValueChange={(v) => setAccountSettings({
                    ...accountSettings,
                    callback_alert_email_template_id: v === '__default__' ? '' : v,
                  })}
                >
                  <SelectTrigger data-testid="callback-alert-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">(texte par défaut)</SelectItem>
                    {emailTemplatesList.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Email envoyé au cabinet quand un client doit être rappelé. Variables disponibles :
                  <code>{'{client_name}'}</code>, <code>{'{client_phone}'}</code>, <code>{'{client_email}'}</code>,
                  <code>{'{type_probleme}'}</code>, <code>{'{preference_rappel}'}</code>,
                  <code>{'{scenario}'}</code>.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleSaveAccountSettings}
            disabled={savingAccount}
            className="rounded-full w-full"
            data-testid="save-account-settings-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {savingAccount ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </TabsContent>

        {/* ─── MARQUE (marque blanche #11) ─── */}
        <TabsContent value="marque" className="mt-6 space-y-6">
          <Card className="border border-border/60">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Building2 className="text-primary" size={24} />
                Votre marque
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Personnalisez l'application avec le nom et le logo de votre cabinet. Ils
                remplacent le logo WorkitDoit en haut à gauche, <strong>une fois connecté</strong>.
                L'écran de connexion et les emails envoyés à vos clients ne sont pas modifiés.
              </p>

              <div className="space-y-2">
                <Label htmlFor="company-name">Nom de la société</Label>
                <Input
                  id="company-name"
                  value={accountSettings.company_name || ''}
                  onChange={(e) => setAccountSettings((prev) => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Ex : Fidelis"
                  maxLength={80}
                  data-testid="company-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  Utilisé comme titre si aucun logo n'est importé. 80 caractères maximum.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Logo</Label>
                {accountSettings.company_logo ? (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                    <img
                      src={accountSettings.company_logo}
                      alt="Logo de la société"
                      className="h-12 w-auto max-w-[160px] object-contain"
                    />
                    <Button
                      variant="outline"
                      onClick={() => setAccountSettings((prev) => ({ ...prev, company_logo: '' }))}
                      data-testid="remove-logo-button"
                    >
                      Retirer le logo
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aucun logo importé — le logo WorkitDoit reste affiché.
                  </p>
                )}
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleLogoFile}
                  data-testid="logo-file-input"
                />
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG, WebP ou SVG — 200 Ko maximum. Un logo sur fond transparent
                  s'affiche mieux sur le bandeau sombre.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleSaveAccountSettings}
            disabled={savingAccount}
            className="rounded-full w-full"
            data-testid="save-branding-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {savingAccount ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Settings;
